import React, { useEffect } from 'react';
import { Toaster } from '../../components/ui/sonner';
import { PageErrorBoundary } from '../../components/ui/error-boundary';
import { useLanguageStore } from '../store/language-store';
import { QueryProvider } from './query-provider';
import { errorService } from '../../shared/lib/error-service';
import './i18n'; // Initialize i18n

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { initializeLanguage } = useLanguageStore();

  useEffect(() => {
    initializeLanguage();
  }, [initializeLanguage]);

  // Global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      errorService.handleError(event.reason, {
        component: 'Global',
        action: 'unhandledRejection',
      });
      
      // Prevent the default browser behavior
      event.preventDefault();
    };

    const handleError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
      
      errorService.handleError(event.error, {
        component: 'Global',
        action: 'globalError',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    // Add global error listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <PageErrorBoundary context="App">
      <QueryProvider>
        {children}
        <Toaster 
          position="top-right"
          richColors
          closeButton
          expand={true}
          duration={4000}
          visibleToasts={5}
          toastOptions={{
            style: {
              background: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              color: 'hsl(var(--foreground))',
            },
            className: 'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
            descriptionClassName: 'group-[.toast]:text-muted-foreground',
          }}
        />
      </QueryProvider>
    </PageErrorBoundary>
  );
};
